import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type UnitKey } from '@fewerbags/core';
import { LOCATION_TYPES, STORAGE_LOCATIONS } from '@fewerbags/schema';
import type { GroceryItemDto, GroceryListDetail, StoreLocation } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { LOCATION_LABEL, type StorageLocation } from '../../lib/pantryDisplay';
import { UNIT_OPTIONS } from '../../lib/units';
import {
  expirationHint,
  expiryWasEdited,
  formatObservedDate,
  formatPriceCents,
  needVsBuyLabel,
  parsePriceInput,
  priceInputValue,
  recentPriceHistory,
} from '../../pages/groceries/display';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { SelectField } from '../SelectField';

type LocationType = StoreLocation['type'];

const STORAGE_OPTIONS = STORAGE_LOCATIONS.map((l) => ({ value: l, label: LOCATION_LABEL[l] }));
const LOCATION_TYPE_OPTIONS = LOCATION_TYPES.map((t) => ({
  value: t,
  label: t === 'aisle' ? 'Aisle' : t === 'endcap' ? 'Endcap' : 'Department',
}));

/**
 * Check-off sheet (PLAN §4.4): confirms quantity, expiration (with its
 * provenance shown), storage location, and — only when the shared layer
 * lacks the data — price and store location. Confirming records the
 * purchase + pantry item and strikes the row through optimistically.
 */
export function CheckOffSheet({
  open,
  onClose,
  listId,
  item,
  storeId,
}: {
  open: boolean;
  onClose: () => void;
  listId: string;
  item: GroceryItemDto | null;
  /** The list's current store — enables the price-history disclosure when set. */
  storeId?: string | null;
}) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('0');
  const [unit, setUnit] = useState<UnitKey>('count');
  const [expiresOn, setExpiresOn] = useState('');
  const [storageLocation, setStorageLocation] = useState<StorageLocation>('pantry');
  const [priceText, setPriceText] = useState('');
  const [locType, setLocType] = useState<LocationType>('aisle');
  const [locValue, setLocValue] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** "Use my own price/location for this store" (PLAN §9 #3b). */
  const [useOwn, setUseOwn] = useState(false);

  const itemId = item?.id ?? '';
  const productId = item?.productId ?? null;
  const detailKey = groceryKeys.detail(listId);

  const defaults = useQuery({
    queryKey: groceryKeys.checkoffDefaults(
      listId,
      itemId,
      item?.productId ?? null,
      item?.buyQty ?? 0,
      item?.unit ?? 'count',
    ),
    queryFn: () =>
      api.groceries.items.checkoffDefaults(listId, itemId, {
        ...(item?.productId ? { productId: item.productId } : {}),
        qty: item?.buyQty ?? 1,
        unit: item?.unit ?? 'count',
      }),
    enabled: open && item !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (!open || !item) return;
    setQty(String(item.buyQty));
    setUnit(item.unit);
    setPhotoFile(null);
    setPhotoNotice(null);
    setHistoryOpen(false);
  }, [open, item]);

  const priceHistory = useQuery({
    queryKey: groceryKeys.priceHistory(storeId ?? '', productId ?? ''),
    queryFn: () => api.stores.priceHistory(storeId as string, productId as string),
    enabled: open && historyOpen && Boolean(storeId) && Boolean(productId),
  });
  const historyPoints = priceHistory.data ? recentPriceHistory(priceHistory.data.points) : [];
  // Once we know there's nothing to show, stop offering the disclosure at all.
  const historyEmpty = priceHistory.isSuccess && priceHistory.data.points.length === 0;
  const canShowHistory = Boolean(storeId) && Boolean(productId) && !historyEmpty;

  useEffect(() => {
    const d = defaults.data;
    if (!d) return;
    setExpiresOn(d.expiresOn ?? '');
    setStorageLocation(d.storageLocation);
    setPriceText(priceInputValue(d.priceCents));
    setUseOwn(
      d.priceSource === 'household_override' || d.location?.source === 'household_override',
    );
    if (d.location) {
      setLocType(d.location.type);
      setLocValue(d.location.value);
    }
  }, [defaults.data]);

  const canUseOwn = Boolean(storeId) && Boolean(productId);
  const ownValues = canUseOwn && useOwn;
  const showLocationFields = (defaults.data?.needsLocation ?? false) || ownValues;
  const showPriceField = (defaults.data?.needsPrice ?? false) || ownValues;

  const check = useMutation({
    mutationFn: async () => {
      // Photo upload is best-effort: a failure here must not block the
      // check-off, it just goes through without the photo (surfaced after).
      let photoMediaId: string | null = null;
      let photoFailed = false;
      if (photoFile) {
        try {
          const media = await api.media.upload(photoFile, { kind: 'ingredient', scope: 'shared' });
          photoMediaId = media.id;
        } catch {
          photoFailed = true;
        }
      }
      const qtyNum = Number(qty);
      const priceCents = parsePriceInput(priceText);
      const result = await api.groceries.items.check(listId, itemId, {
        qty: Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : (item?.buyQty ?? 1),
        unit,
        expiresOn: expiresOn ? expiresOn : null,
        expiresOnEdited: expiryWasEdited(defaults.data?.expiresOn, expiresOn),
        storageLocation,
        ...(item?.productId ? { productId: item.productId } : {}),
        ...(priceCents !== null ? { priceCents } : {}),
        ...(showLocationFields && locValue.trim()
          ? { location: { type: locType, value: locValue.trim() } }
          : {}),
        ...(photoMediaId ? { photoMediaId } : {}),
        ...(ownValues ? { saveOverride: true } : {}),
        clientKey: crypto.randomUUID(),
      });
      return { result, photoFailed };
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<GroceryListDetail>(detailKey);
      qc.setQueryData<GroceryListDetail>(detailKey, (old) =>
        old
          ? {
              ...old,
              list: { ...old.list, checkedCount: old.list.checkedCount + 1 },
              items: old.items.map((i) =>
                i.id === itemId ? { ...i, checkedAt: new Date().toISOString() } : i,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
    },
    onSuccess: ({ result, photoFailed }) => {
      qc.setQueryData<GroceryListDetail>(detailKey, (old) =>
        old
          ? { ...old, items: old.items.map((i) => (i.id === result.item.id ? result.item : i)) }
          : old,
      );
      if (photoFailed) {
        // The item is checked off; keep the sheet open just to show the notice.
        setPhotoNotice("Checked off, but the photo couldn't be uploaded.");
      } else {
        onClose();
      }
    },
  });

  if (!item) {
    return (
      <Dialog open={open} onClose={onClose} title="Check off item">
        <p className="text-sm text-muted">No item selected.</p>
      </Dialog>
    );
  }

  const needVsBuy = needVsBuyLabel(item);

  return (
    <Dialog open={open} onClose={onClose} title={`Check off ${item.ingredientName}`}>
      <div className="flex flex-col gap-4">
        {needVsBuy ? <p className="text-sm font-medium text-ink">{needVsBuy}</p> : null}
        {defaults.isPending ? <p className="text-sm text-muted">Loading defaults…</p> : null}
        {defaults.isError ? <Alert tone="error">{messageForError(defaults.error)}</Alert> : null}

        <div className="grid grid-cols-2 gap-3">
          <Field
            id="checkoff-qty"
            label="Quantity"
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <SelectField
            id="checkoff-unit"
            label="Unit"
            options={UNIT_OPTIONS}
            value={unit}
            onChange={(e) => setUnit(e.target.value as UnitKey)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Field
            id="checkoff-expires"
            label="Expiry date"
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
          {defaults.data ? (
            <p className="text-xs text-muted">
              {expirationHint(defaults.data.expirationRule, defaults.data.expirationBasis)}
            </p>
          ) : null}
        </div>

        <SelectField
          id="checkoff-location"
          label="Storage location"
          options={STORAGE_OPTIONS}
          value={storageLocation}
          onChange={(e) => setStorageLocation(e.target.value as StorageLocation)}
        />

        {canUseOwn && defaults.data ? (
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={useOwn}
              onChange={(e) => setUseOwn(e.target.checked)}
              className="size-5 accent-brand"
            />
            Use my own price and location for this store
          </label>
        ) : null}

        {showPriceField ? (
          <Field
            id="checkoff-price"
            label="Price (optional)"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={priceText}
            onChange={(e) => setPriceText(e.target.value)}
          />
        ) : null}

        {canShowHistory ? (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              aria-expanded={historyOpen}
              className="flex min-h-9 items-center gap-1 text-left text-sm font-medium text-brand"
            >
              <span aria-hidden>{historyOpen ? '▲' : '▼'}</span>
              <span>Price history</span>
            </button>
            {historyOpen ? (
              priceHistory.isPending ? (
                <p className="text-xs text-muted">Loading…</p>
              ) : priceHistory.isError ? (
                <p className="text-xs text-fail">Couldn’t load price history.</p>
              ) : (
                <ul className="flex flex-col gap-0.5 text-xs text-muted">
                  {historyPoints.map((p, i) => (
                    <li key={`${p.observedAt}-${i}`} className="flex justify-between gap-2">
                      <span>{formatObservedDate(p.observedAt)}</span>
                      <span className="text-ink">{formatPriceCents(p.priceCents)}</span>
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </div>
        ) : null}

        {defaults.data?.needsPhoto ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="checkoff-photo" className="text-sm font-medium text-ink">
              Photo (optional)
            </label>
            <input
              id="checkoff-photo"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              className="min-h-11 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink file:mr-3 file:min-h-9 file:rounded-lg file:border-0 file:bg-brand-soft file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand"
            />
            {photoFile ? <p className="text-xs text-muted">Selected: {photoFile.name}</p> : null}
          </div>
        ) : null}

        {photoNotice ? <Alert tone="warning">{photoNotice}</Alert> : null}

        {showLocationFields ? (
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              id="checkoff-loc-type"
              label="Where in the store?"
              options={LOCATION_TYPE_OPTIONS}
              value={locType}
              onChange={(e) => setLocType(e.target.value as LocationType)}
            />
            <Field
              id="checkoff-loc-value"
              label={
                locType === 'department'
                  ? 'Department'
                  : `${locType === 'aisle' ? 'Aisle' : 'Endcap'} #`
              }
              value={locValue}
              onChange={(e) => setLocValue(e.target.value)}
            />
          </div>
        ) : null}

        {check.isError ? <Alert tone="error">{messageForError(check.error)}</Alert> : null}

        <div className="flex gap-2">
          {photoNotice ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button onClick={() => check.mutate()} disabled={check.isPending}>
                {check.isPending ? 'Saving…' : 'Confirm'}
              </Button>
              <Button variant="secondary" onClick={onClose} disabled={check.isPending}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
